import { nanoid } from '#lib/util.js';

/**
 * BLOCK CLIPBOARD
 *
 * Blocks travel between pages (and between browser tabs) as JSON on the system clipboard.
 *
 * Everything a block needs is already in its `to_json()`: entry previews are global entry_ids
 * that re-resolve when the model is constructed, sticker sources are asset URLs, and colors and
 * layouts are site-level. The one page-scoped thing a block can carry is an in-page anchor
 * (`#block_id`, see `resolve_link()`), which is what `prepare_blocks_for_insert()` deals with.
 */

const CLIPBOARD_KIND = 'system606/blocks';
const CLIPBOARD_VERSION = 1;

// Fallback for contexts where the async clipboard API is missing - most notably a dev server
// reached over a plain http LAN address, which is not a secure context. Client-side navigation
// keeps this module alive, so a copy still survives the trip to another page.
let memory_buffer = null;

/**
 * Put blocks (plain JSON, as returned by `block.to_json()`) on the clipboard.
 * @param {object[]} blocks
 * @param {{ source_entry_id?: string|number }} [options] - the entry copied from, so a paste can
 *   tell whether in-page anchors still have their targets around.
 */
export function write_blocks_to_clipboard(blocks, { source_entry_id } = {}) {
  const text = JSON.stringify({
    kind: CLIPBOARD_KIND,
    version: CLIPBOARD_VERSION,
    source_entry_id,
    blocks,
  });

  memory_buffer = text;

  // Best effort: when this fails the copy still works within this tab through the buffer above.
  navigator.clipboard?.writeText(text).catch(error => {
    console.info('Could not write blocks to the system clipboard, using the in-memory buffer instead.', error);
  });
}

/** Parse a clipboard payload. Returns null for anything that is not our own block JSON. */
function parse_payload(text) {
  // Cheap reject before parsing: pasted prose is far more common than a block payload.
  if (typeof text !== 'string' || !text.includes(CLIPBOARD_KIND)) return null;
  try {
    const payload = JSON.parse(text);
    if (payload?.kind !== CLIPBOARD_KIND) return null;
    if (!Array.isArray(payload.blocks) || payload.blocks.length === 0) return null;
    return payload;
  } catch {
    return null; // not JSON, so not ours
  }
}

/**
 * Read blocks for a paste. `clipboard_text` comes from the paste event; the in-memory buffer is
 * only consulted when the clipboard carries no text at all, so text copied in between always wins.
 * Callers must only reach for this outside of a text field - see `handle_window_paste`.
 * @returns {{ blocks: object[], source_entry_id?: string|number } | null}
 */
export function read_blocks_from_clipboard(clipboard_text) {
  return parse_payload(clipboard_text) ?? (clipboard_text ? null : parse_payload(memory_buffer));
}

const ANCHOR_HREF_FIELDS = ['primary_link_href', 'secondary_link_href'];
const MARKDOWN_TEXT_FIELDS = ['title', 'description', 'preamble'];
const MARKDOWN_ANCHOR_LINK = /\[([^\]]*)\]\(#([A-Za-z0-9_-]+)\)/g;

/**
 * Clone blocks so they can be inserted somewhere: fresh block_ids, and in-page anchors pointed at
 * something that still exists.
 *
 * An anchor that names a block inside the copied set follows the copy, so a pair of linked
 * sections stays linked to itself. One that points outside the set only survives when the blocks
 * stay on the page owning the target - otherwise it would silently scroll nowhere.
 *
 * @param {object[]} blocks - plain block JSON
 * @param {{ same_entry?: boolean }} [options] - true when inserting back into the entry copied from
 * @returns {{ blocks: object[], dropped_anchor_count: number }}
 */
export function prepare_blocks_for_insert(blocks, { same_entry = false } = {}) {
  const id_map = new Map();
  for (const block of blocks) {
    if (block?.block_id) id_map.set(block.block_id, nanoid());
  }

  let dropped_anchor_count = 0;

  function remap_anchor(block_id) {
    const copied_target = id_map.get(block_id);
    if (copied_target) return copied_target;
    if (same_entry) return block_id;
    dropped_anchor_count++;
    return null;
  }

  const prepared_blocks = blocks.map(block => {
    const prepared = JSON.parse(JSON.stringify(block));
    prepared.block_id = id_map.get(block?.block_id) ?? nanoid();

    for (const field of ANCHOR_HREF_FIELDS) {
      const href = prepared[field];
      if (typeof href !== 'string' || !href.startsWith('#')) continue;
      const target = remap_anchor(href.slice(1));
      // The link title stays: an unset href hides the link on the public page but keeps it
      // visible while editing, so re-pointing it is one click away instead of a lost field.
      prepared[field] = target ? `#${target}` : '';
    }

    for (const field of MARKDOWN_TEXT_FIELDS) {
      if (typeof prepared[field] !== 'string') continue;
      prepared[field] = prepared[field].replace(MARKDOWN_ANCHOR_LINK, (link, text, block_id) => {
        const target = remap_anchor(block_id);
        return target ? `[${text}](#${target})` : text; // unresolvable: keep the words, drop the link
      });
    }

    if (Array.isArray(prepared.stickers)) {
      // Sticker ids are block-local (an {#each} key), so they can stay as they are.
      prepared.stickers = prepared.stickers.map(sticker => {
        if (typeof sticker?.href !== 'string' || !sticker.href.startsWith('#')) return sticker;
        const target = remap_anchor(sticker.href.slice(1));
        const { href, ...rest } = sticker;
        return target ? { ...rest, href: `#${target}` } : rest;
      });
    }

    return prepared;
  });

  return { blocks: prepared_blocks, dropped_anchor_count };
}
